import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const GATEWAYS = [
    { key: 'cod', label: 'Cash on Delivery', note: 'Pay with cash when the order arrives.' },
    { key: 'card', label: 'Credit / Debit Card', note: 'Stripe card payments.' },
    { key: 'alipay', label: 'Alipay', note: 'Redirect-based wallet payment via Stripe.' },
    { key: 'wechatPay', label: 'WeChat Pay', note: 'QR-code wallet payment via Stripe.' },
    { key: 'octopus', label: 'Octopus', note: 'Not yet integrated — requires a separate merchant account with a partner provider (Airwallex, Payment Asia, or Checkout.com).' },
];

const PLATFORMS = [
    { key: 'website', label: 'Website' },
    { key: 'ios', label: 'iOS App' },
    { key: 'android', label: 'Android App' },
];

const PaymentGatewaySettings = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/payment-gateways');
                setSettings(data.gateways);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load payment gateway settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (gatewayKey, platformKey, value) => {
        setSettings((prev) => ({
            ...prev,
            [gatewayKey]: { ...prev[gatewayKey], [platformKey]: value },
        }));
    };

    const saveHandler = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put('/api/v1/admin/payment-gateways', settings);
            setSettings(data.gateways);
            enqueueSnackbar("Payment gateway settings updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update settings", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Payment Gateways | 1313 Mart" />

            <h1 className="text-lg font-medium uppercase mb-4">Payment Gateways</h1>

            <div className="bg-white rounded-xl shadow-lg w-full p-4 sm:p-6 flex flex-col gap-5">
                {GATEWAYS.map((gw) => (
                    <div key={gw.key} className="flex flex-col gap-3 pb-4 border-b last:border-b-0">
                        <div>
                            <p className="font-medium text-gray-800">{gw.label}</p>
                            <p className="text-xs text-gray-400 max-w-md">{gw.note}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {PLATFORMS.map((platform) => (
                                <TextField
                                    key={platform.key}
                                    select
                                    size="small"
                                    label={platform.label}
                                    value={settings[gw.key]?.[platform.key] || 'disabled'}
                                    onChange={(e) => handleChange(gw.key, platform.key, e.target.value)}
                                    sx={{ minWidth: 180 }}
                                >
                                    <MenuItem value="enabled">Enabled</MenuItem>
                                    <MenuItem value="comingSoon">Coming Soon</MenuItem>
                                    <MenuItem value="disabled">Hidden</MenuItem>
                                </TextField>
                            ))}
                        </div>
                    </div>
                ))}

                <button
                    onClick={saveHandler}
                    disabled={saving}
                    className="bg-primary-orange self-start px-6 py-2.5 text-white font-medium rounded shadow hover:shadow-lg disabled:opacity-60"
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </>
    );
};

export default PaymentGatewaySettings;